/**
 * Attribute Definition Service
 * Handles attribute definitions and user-specific attribute values
 */

import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { SIS_SCHEMA_ENDPOINTS } from '../api';
import {
  AttributeHierarchy,
  UserAttributeWithDefinition,
  UpdateUserAttributeResponse,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class AttributeDefinitionService {
  private readonly http = inject(HttpClient);

  // Signals for reactive state management
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly attributeHierarchy = signal<AttributeHierarchy[]>([]);
  readonly userAttributes = signal<UserAttributeWithDefinition[]>([]);

  /**
   * Get hierarchical attribute structure
   * @param visibleOnly - Filter for visible attributes only (default: true)
   */
  getAttributeHierarchy(visibleOnly: boolean = true): Observable<AttributeHierarchy[]> {
    this.loading.set(true);
    this.error.set(null);

    const url = `${SIS_SCHEMA_ENDPOINTS.attributeDefinitions.hierarchy}?visible_only=${visibleOnly}`;

    return this.http.get<AttributeHierarchy[]>(url).pipe(
      tap((hierarchy) => {
        this.attributeHierarchy.set(hierarchy);
        this.loading.set(false);
        console.log(`✅ Loaded ${hierarchy.length} attribute groups`);
      }),
      catchError((error) => this.handleError(error))
    );
  }

  /**
   * Get user-specific attributes with values
   * @param contactId - Contact ID to fetch attributes for
   * @param visibleOnly - Filter for visible attributes only (default: true)
   */
  getUserAttributes(
    contactId: number,
    visibleOnly: boolean = true
  ): Observable<UserAttributeWithDefinition[]> {
    this.loading.set(true);
    this.error.set(null);

    const url = `${SIS_SCHEMA_ENDPOINTS.attributeDefinitions.userAttributes(
      contactId
    )}?visible_only=${visibleOnly}`;

    return this.http.get<UserAttributeWithDefinition[]>(url).pipe(
      tap((attributes) => {
        this.userAttributes.set(attributes);
        this.loading.set(false);
      }),
      catchError((error) => this.handleError(error))
    );
  }

  /**
   * Update or create a user attribute value
   * @param contactId - Contact ID
   * @param attributeId - Attribute definition ID
   * @param value - New value for the attribute
   */
  updateUserAttribute(
    contactId: number,
    attributeId: number,
    value: string
  ): Observable<UpdateUserAttributeResponse> {
    this.loading.set(true);
    this.error.set(null);

    const url = SIS_SCHEMA_ENDPOINTS.attributeDefinitions.updateUserAttribute(
      contactId,
      attributeId
    );

    return this.http
      .put<UpdateUserAttributeResponse>(url, null, {
        params: { value },
      })
      .pipe(
        tap((response) => {
          this.loading.set(false);
        }),
        catchError((error) => this.handleError(error))
      );
  }

  /**
   * Delete a user attribute value
   * @param contactId - Contact ID
   * @param attributeId - Attribute definition ID
   */
  deleteUserAttribute(contactId: number, attributeId: number): Observable<{ message: string }> {
    this.loading.set(true);
    this.error.set(null);

    const url = SIS_SCHEMA_ENDPOINTS.attributeDefinitions.deleteUserAttribute(
      contactId,
      attributeId
    );

    return this.http.delete<{ message: string }>(url).pipe(
      tap(() => {
        this.loading.set(false);
        console.log(`✅ Deleted attribute ${attributeId} for contact ${contactId}`);
      }),
      catchError((error) => this.handleError(error))
    );
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: unknown): Observable<never> {
    this.loading.set(false);

    let errorMessage = 'Ein unbekannter Fehler ist aufgetreten';

    if (error && typeof error === 'object' && 'error' in error) {
      const httpError = error as {
        error?: ErrorEvent | { detail?: string };
        status?: number;
        message?: string;
      };

      if (httpError.error instanceof ErrorEvent) {
        // Client-side error
        errorMessage = `Fehler: ${httpError.error.message}`;
      } else {
        // Server-side error
        const detail =
          httpError.error && typeof httpError.error === 'object' && 'detail' in httpError.error
            ? httpError.error.detail
            : httpError.message;
        errorMessage = `Fehler ${httpError.status || 'unbekannt'}: ${
          detail || 'Keine Details verfügbar'
        }`;
      }
    }

    console.error('❌ Attribute Definition Service Error:', errorMessage);
    this.error.set(errorMessage);

    return throwError(() => new Error(errorMessage));
  }
}
